import { ChangeDetectionStrategy, Component, computed, effect, inject, signal, untracked } from "@angular/core";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { addressBarShows, PricingStore, urlNamesEvent } from "./s25.event.pricing.store";
import { S25PricingSidebarComponent } from "./sidebar/s25.pricing.sidebar.component";
import { S25InvoiceViewComponent } from "./invoice/s25.invoice.component";
import { S25InvoiceFormComponent } from "./invoice/invoice-form/s25.invoice.form.component";
import { S25AccountSummaryComponent } from "./account-summary/s25.account.summary.component";
import { S25OptModule } from "../../s25-opt/s25.opt.module";
import { OptUtil } from "../../s25-opt/s25.opt.util";
import { s25Resource } from "../../../util/s25.resource";
import { Bind } from "../../../decorators/bind.decorator";
import { S25LoadingInlineStaticComponent } from "../../s25-loading/s25.loading.inline.static.component";
import { StateService } from "../../../services/state.service";
import { StateStore } from "../../../services/state/state.store";
import { S25States } from "../../../services/state/state.map";
import { ColumnChooserModelI } from "../../s25-column-chooser/s25.column.chooser.component";
import { PricingColumns } from "./pricing.columns";
import { PaymentsStore } from "./payments/s25.payments.store";

@TypeManagerDecorator("s25-ng-event-pricing")
@Component({
    selector: "s25-ng-event-pricing",
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        S25PricingSidebarComponent,
        S25InvoiceViewComponent,
        S25InvoiceFormComponent,
        S25AccountSummaryComponent,
        S25OptModule,
        S25LoadingInlineStaticComponent,
    ],
    providers: [PaymentsStore],
    template: `
        @if (optBean()) {
            <s25-ng-opt [modelBean]="optBean()!" />
        }

        @if (loadError()) {
            <p class="cn-alert cn-alert--danger" role="alert">{{ loadError() }}</p>
        } @else if (pricingBuilding() || initialLoading()) {
            <s25-ng-loading-inline-static />
            @if (pricingBuilding()) {
                <p class="pricing-status" role="status">Building pricing for this event...</p>
            }
        } @else if (pricingUnavailable()) {
            <p class="pricing-status" role="status">
                Pricing is not available for this event. Please try again in a few minutes.
            </p>
        } @else {
            <!-- Mounted above both views, since either can ask for the form. -->
            <s25-ng-invoice-form />

            <div class="pricing-layout" [class.refetching]="refetching()" [attr.aria-busy]="refetching()">
                <s25-pricing-sidebar />
                @if (sidebarView() === "summary") {
                    <s25-ng-account-summary />
                } @else {
                    <s25-ng-invoice-view />
                }
            </div>
        }
    `,
    host: { class: "pricing-wrapper" },
    styles: `
        s25-ng-loading-inline-static {
            display: block;
            width: fit-content;
            margin: auto;
        }

        .pricing-status {
            padding: 1rem 0;
            text-align: center;
        }

        .pricing-layout {
            display: flex;
            gap: 1rem;
        }

        .pricing-layout.refetching {
            opacity: 0.6;
            transition: opacity var(--transition-medium);
        }

        /* The sidebar owns its own width so that collapsing it can narrow the host. */

        .pricing-layout s25-ng-invoice-view,
        .pricing-layout s25-ng-account-summary {
            flex: 1;
        }
    `,
})
export class S25EventPricingComponent {
    private readonly pricingStore = inject(PricingStore);
    private readonly stateStore = inject(StateStore);

    readonly eventId = this.pricingStore.eventId;
    readonly combineRelatedEvents = this.pricingStore.combineRelated;
    readonly hasRelatedEvents = this.pricingStore.hasRelatedEvents;
    readonly sidebarView = this.pricingStore.sidebarView;

    /** Held from asking for a URL until the last ask has landed, since the address bar lags it. */
    private readonly urlSyncPending = signal(false);

    private readonly colChooseBean: ColumnChooserModelI = {
        colList: PricingColumns.toColumnChooserList(
            PricingColumns.pricingColumns(this.combineRelatedEvents()),
            this.pricingStore.hiddenColumns(),
        ),
        updateCallback: () => this.onColumnsChosen(),
    };

    constructor() {
        // allow pricing state to reflect the url when manually entered or linked
        effect(() => {
            const eventId = this.pricingStore.eventId();
            const selected = this.pricingStore.evBillId();
            const target = this.sidebarView() === "summary" ? undefined : selected;
            // Until the invoices are in, a link naming one reads as a link naming none, and syncing
            // that would rewrite the URL out from under it.
            if (!eventId || !this.pricingStore.invoicesLoaded()) return;
            // eventPricing is sticky, so this component outlives a trip elsewhere in the app and
            // this effect keeps running from another page, and from another event's pricing page.
            if (this.stateStore.currentStateName() !== S25States.names.eventPricing) return;
            if (!urlNamesEvent(eventId)) return;
            // account for timing between url and state service recognition
            if (this.urlSyncPending()) return;
            const wanted = StateService.href(S25States.names.eventPricing, {
                itemId: eventId,
                evBillId: target ?? "",
            });
            if (!wanted || addressBarShows(wanted)) return;
            this.urlSyncPending.set(true);
            StateService.go(
                S25States.names.eventPricing,
                { itemId: eventId, evBillId: target ?? "" },
                { location: "replace" },
            )
                .then(async () => {
                    // A transition to params the router already holds has nothing to do, the address
                    // bar included, which is how a URL typed for a page already open stays as typed.
                    if (!addressBarShows(wanted)) await StateService.replaceUrl(wanted);
                })
                .finally(() => this.urlSyncPending.set(false));
        });

        effect(() => {
            const columns = PricingColumns.pricingColumns(this.combineRelatedEvents());
            this.colChooseBean.setCols?.(
                PricingColumns.toColumnChooserList(columns, untracked(this.pricingStore.hiddenColumns)),
            );
        });
    }

    private onColumnsChosen() {
        this.pricingStore.setHiddenColumns(
            this.colChooseBean.colList.filter((column) => !column.isVisible).map((column) => column.prefname),
        );
    }

    private readonly optBeanResource = s25Resource({
        if: () => !!this.eventId() && this.pricingStore.relatedEventsResolved(),
        params: () => ({ itemId: this.eventId()!, hasRelatedEvents: this.hasRelatedEvents() }),
        loader: ({ params }) =>
            OptUtil.formPricingBean(this.refresh, {
                itemId: params.itemId,
                ...(params.hasRelatedEvents && { hasCombineRelatedEventsCheckbox: true }),
                colChooseBean: this.colChooseBean,
            }),
    });

    protected readonly optBean = this.optBeanResource.value;

    readonly loadError = computed(() =>
        this.pricingStore.invoicesLoaded() ? "" : (this.pricingStore.invoiceError()?.message ?? ""),
    );
    readonly initialLoading = computed(
        () => !!this.eventId() && !this.pricingStore.invoicesLoaded() && !this.loadError(),
    );
    readonly refetching = computed(() => this.pricingStore.invoicesLoaded() && this.pricingStore.invoicesLoading());
    readonly pricingBuilding = this.pricingStore.pricingBuilding;
    readonly pricingUnavailable = this.pricingStore.pricingUnavailable;

    @Bind
    refresh() {
        const bean = this.optBean();

        // Hiding no-charge items only filters rows the view already has, so it never refetches.
        const hideNoChargeItems = !!bean?.hideNoChargeItems;
        if (hideNoChargeItems !== this.pricingStore.hideNoChargeItems()) {
            return this.pricingStore.setHideNoChargeItems(hideNoChargeItems);
        }

        const combine = !!bean?.combineRelatedEvents;
        if (combine === this.combineRelatedEvents()) return this.pricingStore.reloadInvoices();
        this.pricingStore.setCombineRelated(combine);
    }
}
